/**
 * Settings → Folders (web.md §4, fix 8): name, icon, description, retention (days or forever) and
 * "counts as kept". No colour: folder icons use the neutral token everywhere. Built-in folders can be
 * renamed and re-configured but never archived (the shell disables Archive for `builtin` items).
 */

import type { Folder, FolderId } from '@digester/core';
import { useEngine, useEngineQuery } from '../../engine-client/EngineProvider.js';
import { qk } from '../../engine-client/keys.js';
import { IconField } from '../../icons/IconField.js';
import { Field, Section, TextArea, Toggle } from '../../ui/form.js';
import '../rules/rules.css';
import { EditorEmpty, EditorFrame, useDraft, type ItemsResult, type SettingsCategory, type SettingsEditorProps } from '../shell/index.js';

interface FolderDraft {
  name: string;
  icon: string;
  description: string;
  forever: boolean;
  retentionDays: number;
  isKeep: boolean;
}

const DEFAULT_RETENTION_DAYS = 30;

function toDraft(f: Folder): FolderDraft {
  return { name: f.name, icon: f.icon, description: f.description, forever: f.retentionDays === null, retentionDays: f.retentionDays ?? DEFAULT_RETENTION_DAYS, isKeep: f.isKeep };
}

function retentionText(f: Folder): string {
  return f.retentionDays === null ? 'Kept forever' : `Kept ${f.retentionDays} day${f.retentionDays === 1 ? '' : 's'}`;
}

function useItems(): ItemsResult {
  const q = useEngineQuery(qk.folders(), (engine) => engine.folders.list());
  return {
    items: (q.data ?? []).map((f) => ({ id: f.id, name: f.name, icon: f.icon, subtitle: f.description || retentionText(f), builtin: f.builtin })),
    isLoading: q.isLoading,
    error: q.error ?? null,
  };
}

function FolderEditor({ recordId, duplicateOf }: SettingsEditorProps) {
  const q = useEngineQuery(qk.folders(), (engine) => engine.folders.list());
  if (!recordId) return <EditorEmpty />;
  if (q.isLoading) return <EditorEmpty text="Loading…" />;
  const folders = q.data ?? [];
  if (recordId === 'new') {
    const from = duplicateOf ? folders.find((f) => f.id === duplicateOf) : undefined;
    const initial: FolderDraft = from
      ? { ...toDraft(from), name: `${from.name} copy` }
      : { name: '', icon: 'folder', description: '', forever: false, retentionDays: DEFAULT_RETENTION_DAYS, isKeep: false };
    return <FolderForm folder={null} initial={initial} />;
  }
  const folder = folders.find((f) => f.id === recordId);
  if (!folder) return <EditorEmpty />;
  return <FolderForm folder={folder} initial={toDraft(folder)} />;
}

function FolderForm({ folder, initial }: { folder: Folder | null; initial: FolderDraft }) {
  const engine = useEngine();
  const draft = useDraft<FolderDraft>({
    initial,
    isNew: folder === null,
    onSave: async (v) => {
      const input = { name: v.name.trim(), icon: v.icon as never, description: v.description.trim(), retentionDays: v.forever ? null : Math.round(v.retentionDays), isKeep: v.isKeep };
      const saved = folder ? await engine.folders.update(folder.id, input) : await engine.folders.create(input);
      return saved.id;
    },
  });
  const v = draft.value;
  return (
    <EditorFrame title={v.name.trim() || folder?.name || 'New folder'} icon={v.icon}>
      <Section title="Folder">
        <Field label="Name" name="name" value={v.name} required maxLength={80} onChange={(name) => draft.set((d) => ({ ...d, name }))} />
        <IconField label="Icon" name="icon" value={v.icon} onChange={(icon) => draft.set((d) => ({ ...d, icon }))} />
        <TextArea label="Description" name="description" value={v.description} onChange={(description) => draft.set((d) => ({ ...d, description }))} />
        <Toggle
          label="Counts as kept"
          name="isKeep"
          help="Messages you read here or move here count as kept for the usefulness of their source."
          checked={v.isKeep}
          onChange={(isKeep) => draft.set((d) => ({ ...d, isKeep }))}
        />
      </Section>
      <Section title="Retention">
        <Toggle label="Keep messages forever" name="forever" checked={v.forever} onChange={(forever) => draft.set((d) => ({ ...d, forever }))} />
        {v.forever ? null : (
          <Field
            label="Keep messages for · days"
            name="retention"
            type="number"
            min={1}
            max={3650}
            required
            value={v.retentionDays}
            onChange={(days) => draft.set((d) => ({ ...d, retentionDays: Number(days) }))}
          />
        )}
        <p className="field-help">After that, a message becomes a stub: its source statistics and history stay, its text is removed.</p>
      </Section>
      {folder?.builtin ? <div className="editor-note">Built-in folder. Its identity remains stable when renamed, and it cannot be archived.</div> : null}
    </EditorFrame>
  );
}

export const category: SettingsCategory = {
  id: 'folders',
  name: 'Folders',
  icon: 'folder',
  useItems,
  Editor: FolderEditor,
  newLabel: 'New folder',
  duplicate: true,
  archive: (engine, item) => engine.folders.archive(item.id as FolderId),
  archiveNote: 'Its messages return to Inbox with their history.',
};
